import { useCallback, useRef, useState } from 'react';
import { useEffect, useMemo } from 'react';
import { DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { captureMessage } from '@sentry/react';

import { getChatUrl } from '../events/error/utils';
import { WORKFLOW_BUILDER_QUERY_PARAM_KEY } from '../workflow-builder/intent-workflows-table/constants';
import { getWorkflowId } from '../workflow-builder/intent-workflows-table/helper';
import { useSolveMetricsQueryParams } from '../workflow-builder/intent-workflows-table/hooks/useSolveMetricsQueryParams';
import { getWorkflowValueFromSearch } from './autonomous-agent/helpers';
import { JOURNEY_MAP } from './email-builder-page/intent-email-builder/primitiveConstants';
import { TEMPLATE_INTENT_ID, WIDGET, WidgetMode } from './constants';
import { BuilderView, EmailMode } from './types';
import { formatIntentNames } from './utils';
import isEmpty from 'lodash/fp/isEmpty';
import { INTENT_FILTER_PARAM_NAME } from 'src/components/dashboard-pages/solve-insights/constants';
import { dateRangeToTimestamp } from 'src/components/dashboard-pages/solve-insights/helpers';
import useTabs from 'src/components/dashboard-pages/solve-insights/hooks/useTabs';
import { CONVERSATION_OAUTH_CLIENT_CV_PREFIX } from 'src/constants/solve';
import {
  useGetIntentsQueryWithProduct,
  useIsFeatureFlagEnabled,
  useLoadWorkflowBuilderFromURL,
} from 'src/hooks/hooks';
import { useGetContextVariables } from 'src/hooks/useGetContextVariables';
import { transformActionsForCanvas } from 'src/reducers/actionBuilderReducer/actionBuilderReducer';
import {
  selectCanvasWorkflowBuilder,
  selectIntentId,
  selectIntentTitle,
  selectInvalidGoToSteps,
  selectIsBranchValid,
  selectUndefinedContextVariables,
  selectUnsupportedSteps,
  selectVersion,
} from 'src/reducers/workflowBuilderReducer/workflowBuilderReducer';
import {
  useGetActionBuilderActionsQuery,
  useGetConversationOauthClientsQuery,
} from 'src/services/action-builder/actionBuilderApi';
import { useGetFeatureFlagsQuery } from 'src/services/dashboard-api';
import { useGetOrCreateAutopilotWorkflowQuery } from 'src/services/email-autopilot/emailAutopilotApi';
import { useGetWorkflowsForIntentQuery } from 'src/services/workflow-builder-canvas/workflowBuilderCanvasApi';
import {
  useClaimBuildingBlockMutation,
  useCreateIntentMutation,
  useLazyCheckBuildingBlockPrepQuery,
} from 'src/services/workflow-builder-intent/workflowBuilderIntentApi';
import { useGetHandoffConfigurationsQuery } from 'src/services/workflow-builder-metrics';
import {
  useGetAutoflowTemplatesQuery,
  useGetAutoflowWorkflowQuery,
} from 'src/services/workflowBuilderAutoflowApi.ts/workflowBuilderAutoflowApi';
import {
  CanvasWorkflowBuilderState,
  setTargetStepId,
} from 'src/slices/canvas-workflow-builder/workflowBuilderSlice';
import {
  getIntent,
  getLatestWorkflowVersions,
  getWorkflowVersion,
  loadDraftWorkflow,
} from 'src/slices/canvas-workflow-builder/workflowBuilderSlice.thunks';
import {
  getIntentEmailConfiguration,
  obtainIntentEmailConfiguration,
  selectEmailBuilderState,
} from 'src/slices/email-builder/emailBuilderSlice';
import {
  selectEmailWorkflowState,
  setEmailTargetStepId,
  validateEmailWorkflow,
} from 'src/slices/email-workflow/emailWorkflowSlice';
import { useAppDispatch } from 'src/store/hooks';
import { SolveWidgetProduct } from 'src/types/types';
import { CHANNEL_TO_PRODUCT_MAP } from 'src/utils/constants';
import { CommonIntentWorkflowType, Routes } from 'src/utils/enums';
import { getUndefinedOauthSignInClientNames } from 'src/utils/oauthSignIn';
import { initializePusher } from 'src/utils/pusherUtils';
import { isEffectivelyAutoflow } from 'src/utils/solve/intentUtils';
import { handleSwapWhenHovering } from 'src/utils/solve/stepUtils';
import { last30DaysTimeRange } from 'src/utils/timeRangeHelpers';

export function useGetBuilderQueryParams() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const isWorkflowBuilder = pathname === Routes.WORKFLOW_BUILDER_EDIT;
  // if the intent id is not set in the url, we wait for the workflow
  // to load then use that intent id and update the query params
  const reduxIntentId = useSelector(selectIntentId);
  const { data, isLoading } = useGetWorkflowsForIntentQuery(
    { intentDefinitionId: reduxIntentId },
    { skip: !reduxIntentId },
  );

  const view = useMemo(() => {
    const query = new URLSearchParams(search);
    const configurationId = query.get('configurationId') || '';
    const view = (query.get('view') as BuilderView) || (WIDGET as BuilderView);
    const workflowId = query.get('workflowId') || '';
    const intentId = query.get('intentId') || '';
    const stepId = query.get('stepId') || '';
    const stepWorkflowId = query.get('stepWorkflowId') || '';

    return {
      configurationId,
      intentId,
      stepId,
      stepWorkflowId,
      view,
      workflowId,
    };
  }, [search]);

  // When intentId is in the URL but workflowId is not, resolve the workflowId
  // by fetching workflows for that intent.
  const needsWorkflowId =
    isWorkflowBuilder && !!view.intentId && !view.workflowId;
  const { data: workflowsFromUrlIntent, isLoading: isLoadingUrlIntent } =
    useGetWorkflowsForIntentQuery(
      { intentDefinitionId: view.intentId },
      { skip: !needsWorkflowId },
    );

  useEffect(() => {
    if (!isWorkflowBuilder) {
      return;
    }

    if (!view.intentId && !isLoading && data) {
      const query = new URLSearchParams(search);
      const intentId = reduxIntentId;
      query.set('intentId', intentId);
      query.set('workflowId', getWorkflowId(view.view, data));
      navigate(
        {
          search: query.toString(),
        },
        { replace: true },
      );
    }

    // Handle case where intentId is present but workflowId is missing
    if (needsWorkflowId && !isLoadingUrlIntent && workflowsFromUrlIntent) {
      const query = new URLSearchParams(search);
      query.set('intentId', view.intentId);
      query.set('workflowId', getWorkflowId(view.view, workflowsFromUrlIntent));
      navigate(
        {
          search: query.toString(),
        },
        { replace: true },
      );
    }
  }, [
    view.intentId,
    view.view,
    view.workflowId,
    data,
    isLoading,
    search,
    navigate,
    isWorkflowBuilder,
    reduxIntentId,
    needsWorkflowId,
    isLoadingUrlIntent,
    workflowsFromUrlIntent,
  ]);
  return view;
}

/**
 * Construct the URL for cases where we are not able te get intent id and workflow id right away.
 * This behavior can occur whenever a new intent workflow is created or new a common intent workflow
 * is added.
 */
export function useConstructUrlOnIntetWorkflowCreation() {
  const navigate = useNavigate();
  const canvasData = useSelector(selectCanvasWorkflowBuilder);
  const {
    intentId: intentIdQueryParam,
    view: currentView,
    workflowId: workflowIdQueryParam,
  } = useGetBuilderQueryParams();
  const workflowBuilderDataIntentId = canvasData.intent_id;
  const workflowBuilderDataWorkflowId =
    currentView === 'api'
      ? canvasData.api_workflow_id
      : canvasData.intent_workflow_id;

  const isDataLoadedCorrectly =
    workflowBuilderDataIntentId && workflowBuilderDataWorkflowId;

  const areQueryParamsIncomplete = !workflowIdQueryParam;

  const shouldUpdateQueryParams =
    isDataLoadedCorrectly && areQueryParamsIncomplete;

  useEffect(() => {
    if (shouldUpdateQueryParams) {
      const queryParams = {
        /**
         * If a common intent is added we will get the intent id for free from the URL,
         * if not, we will need to get from the API request.
         */
        intentId: intentIdQueryParam
          ? intentIdQueryParam
          : workflowBuilderDataIntentId || '',
        view: currentView,
        workflowId: workflowBuilderDataWorkflowId,
      };
      navigate(
        {
          search: `?${new URLSearchParams(queryParams).toString()}`,
        },
        { replace: true },
      );
    }
  }, [
    shouldUpdateQueryParams,
    workflowBuilderDataIntentId,
    workflowIdQueryParam,
    intentIdQueryParam,
    workflowBuilderDataWorkflowId,
    currentView,
    navigate,
  ]);
}

export const useIntentEmailConfiguration = ({
  configurationId,
  intentId,
  isEmailBuilderView,
}: {
  configurationId: string;
  intentId: string;
  isEmailBuilderView: boolean;
}) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isEmailBuilderView) {
      return;
    }
    if (configurationId) {
      dispatch(getIntentEmailConfiguration({ configurationId }));
      return;
    } else {
      dispatch(
        obtainIntentEmailConfiguration({ intentDefinitionId: intentId }),
      );
    }
  }, [configurationId, dispatch, intentId, isEmailBuilderView]);

  return useSelector(selectEmailBuilderState);
};

export const useGetIntentTitle = (intentId: string) => {
  const dispatch = useAppDispatch();
  const intentTitle = useSelector(selectIntentTitle);
  const isTemplateIntentWorkflow = useGetIsTemplateIntentWorkflow();
  const { template } = useGetTemplateAutoflowWorkflow();

  useEffect(() => {
    if (intentId && !isTemplateIntentWorkflow) {
      dispatch(getIntent(intentId));
    }
  }, [dispatch, intentId, intentTitle, isTemplateIntentWorkflow]);

  return template?.name ?? intentTitle;
};

export const useGetValidationErrors = () => {
  const dispatch = useAppDispatch();
  const { emailWorkflow } = useSelector(selectEmailWorkflowState);
  const { email_workflow_id, last_modified_date, step_map, version } =
    emailWorkflow || {};

  useEffect(() => {
    if (email_workflow_id) {
      dispatch(
        validateEmailWorkflow({
          emailWorkflowId: email_workflow_id,
          version: version || -1,
        }),
      );
    }
  }, [dispatch, last_modified_date, step_map, version, email_workflow_id]);
};

export const useGoBackToLandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { view } = useGetBuilderQueryParams();

  return useCallback(() => {
    const search = window.localStorage.getItem(
      WORKFLOW_BUILDER_QUERY_PARAM_KEY,
    );
    window.localStorage.removeItem(WORKFLOW_BUILDER_QUERY_PARAM_KEY);
    const params = new URLSearchParams(search || '');
    if (view === 'playbook') {
      // assist workflows are now under the assist tab
      // so we need to set the channel to playbook
      params.set('channel', 'playbook');
    }

    /**
     * When creating intents or if WORKFLOW_BUILDER_QUERY_PARAM_KEY
     * does not exist, we can use the view as the channel
     */
    if (!params.size) {
      params.set('channel', view);
    }

    // Preserve the editor's `header` query param (e.g. `outbound`) so the
    // landing page lands on the correct sub-tab. Without this, an outbound
    // voice workflow's back button would drop the user on Inbound.
    const editorHeader = new URLSearchParams(location.search).get('header');
    if (editorHeader) {
      params.set('header', editorHeader);
    }

    navigate({
      pathname:
        view === 'playbook'
          ? Routes.ASSIST_WORKFLOW_BUILDER
          : Routes.WORKFLOW_BUILDER,
      search: params.toString(),
    });
  }, [navigate, view, location.search]);
};

export const useCheckForToasts = () => {
  const location = useLocation();

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  useEffect(() => {
    const checkForToasts = () => {
      const query = new URLSearchParams(location.search);
      if (query.get('from') === 'createIntent') {
        setToastMessage('New intent is added.');
      } else if (query.get('from') === 'updateIntent') {
        setToastMessage('Intent has been updated.');
      }
    };

    checkForToasts();
  }, [location.search]);

  return { setToastMessage, toastMessage };
};

export const useInitPusher = () => {
  useEffect(() => {
    initializePusher();
  }, []);
};

export const useWorkflowBuilderMode = () => {
  const navigate = useNavigate();
  const version = useSelector(selectVersion);
  const { pathname, search } = useLocation();

  // Only fetch data when on workflow builder pages to avoid unnecessary API calls
  const isWorkflowBuilderPage =
    pathname === Routes.WORKFLOW_BUILDER_EDIT ||
    pathname === Routes.WORKFLOW_BUILDER;

  const { data } = useGetIntentsQueryWithProduct(undefined, {
    skip: !isWorkflowBuilderPage,
  });
  const { intents = [] } = data ?? {};
  const intentId = getWorkflowValueFromSearch(search, 'intentId');
  const { data: featureFlagsData } = useGetFeatureFlagsQuery();
  const { feature_flags: featureFlags = [] } = featureFlagsData ?? {};
  const isAutonomousAgentEnabled = featureFlags.includes(
    'autonomous_agent_enabled',
  );

  const selectedIntent = useMemo(() => {
    return intents?.find(intent => intent.intent_definition_id === intentId);
  }, [intentId, intents]);
  const isAutoflow = selectedIntent?.is_autoflow;
  const isHandoff = selectedIntent?.is_handoff;

  const isKnowledge =
    selectedIntent?.intent_definition_id ===
    CommonIntentWorkflowType.KNOWLEDGE_ARTICLE;
  const isOtherQuestions =
    selectedIntent?.intent_definition_id ===
    CommonIntentWorkflowType.GENERAL_HANDOFF;

  const updateWorkflowBuilderMode = useCallback(
    (mode: WidgetMode) => {
      const mergedParams = new URLSearchParams({
        ...Object.fromEntries(new URLSearchParams(search)),
        ...{ mode },
      }).toString();
      navigate({
        search: `?${mergedParams.toString()}`,
      });
    },
    [navigate, search],
  );

  // Memoize mode calculation to avoid recalculating on every render
  const mode = useMemo(() => {
    const paramMode = new URLSearchParams(search).get('mode');
    if (paramMode) {
      return paramMode as WidgetMode;
    }

    /**
     * If mode does not exist in the URL, we will go based off this logic
     * 1. Return classic for handoffs/knowledge/other questions
     * 2. isAutoflow = true => "autoflow"
     * 3. isAutoflow = false
     *    a. classic workflow version <= 2 aka has no live workflow && autoflow is enabled => "autoflow"
     *    b. other cases are all "classic"
     */
    if (isHandoff || isKnowledge || isOtherQuestions) {
      return 'classic';
    }
    if (isAutoflow || (version <= 2 && isAutonomousAgentEnabled)) {
      return 'autoflow';
    }

    return 'classic';
  }, [
    search,
    isHandoff,
    isKnowledge,
    isOtherQuestions,
    isAutoflow,
    version,
    isAutonomousAgentEnabled,
  ]);

  return [mode, updateWorkflowBuilderMode] as const;
};

/**
 * True when the classic builder is open for an intent that
 * `classic_workflows_deprecated` now treats as autoflow — the runtime uses the
 * autoflow version, so edits here won't take effect. Drives the migrated-classic
 * banner and the hiding of the publish/save/preview controls. `isLoading`
 * reflects the intents query so the banner doesn't flicker before the mode is
 * known.
 */
export const useIsMigratedClassic = (): {
  isLoading: boolean;
  isMigrated: boolean;
} => {
  const { search } = useLocation();
  const { data, isLoading } = useGetIntentsQueryWithProduct();
  const intentId = getWorkflowValueFromSearch(search, 'intentId');
  const isClassicWorkflowsDeprecated = useIsFeatureFlagEnabled(
    'classic_workflows_deprecated',
  );
  const [builderMode] = useWorkflowBuilderMode();

  const selectedIntent = data?.intents?.find(
    intent => intent.intent_definition_id === intentId,
  );

  const isMigrated =
    isClassicWorkflowsDeprecated &&
    builderMode === 'classic' &&
    isEffectivelyAutoflow(
      intentId,
      selectedIntent?.is_autoflow,
      isClassicWorkflowsDeprecated,
    );

  return { isLoading, isMigrated };
};

export const useEmailMode = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const emailMode = (query.get('emailMode') || JOURNEY_MAP) as EmailMode;
  const { intentId } = useGetBuilderQueryParams();

  const { data: workflowsForIntent, isLoading } = useGetWorkflowsForIntentQuery(
    {
      intentDefinitionId: intentId,
    },
    {
      skip: !intentId,
    },
  );

  const setEmailMode = useCallback(
    (tab: EmailMode) => {
      const queryParams = new URLSearchParams(search);
      const workflowId =
        tab === 'Interactive'
          ? workflowsForIntent?.interactive_email_workflow_id
          : workflowsForIntent?.widget_workflow_id;
      queryParams.set('workflowId', workflowId as string);
      queryParams.set('emailMode', tab);
      navigate({
        search: queryParams.toString(),
      });
    },
    [navigate, search, workflowsForIntent],
  );

  return { emailMode, isLoading, setEmailMode };
};

export const useGetAgentProductFromChannel = (): SolveWidgetProduct => {
  const { view } = useGetBuilderQueryParams();
  const { emailMode } = useEmailMode();

  return useMemo(() => {
    if (view === 'email') {
      return emailMode === 'Interactive' ? 'interactive_email' : 'email';
    }
    return CHANNEL_TO_PRODUCT_MAP[view];
  }, [view, emailMode]);
};

export const useInitCanvas = () => {
  const dispatch = useAppDispatch();
  const isTemplate = useGetIsTemplateIntentWorkflow();
  const { isLoading: isContextVariablesLoading } = useGetContextVariables();

  const [showConfirmation, setShowConfirmation] = useState(false);
  const hasShownConfirmationRef = useRef(false);

  const { stepId, workflowId: selectedWorkflowId } = useGetBuilderQueryParams();
  const canvasData: CanvasWorkflowBuilderState = useSelector(
    selectCanvasWorkflowBuilder,
  );

  useLoadWorkflowBuilderFromURL();

  useEffect(() => {
    if (stepId) {
      dispatch(setTargetStepId(stepId));
    }

    return () => {
      dispatch(setTargetStepId(null));
    };
  }, [dispatch, stepId]);

  // Check if stepId exists in loaded workflow, if not show confirmation to load a saved version (only once)
  useEffect(() => {
    if (
      isTemplate ||
      !stepId ||
      !selectedWorkflowId ||
      isEmpty(canvasData.steps)
    ) {
      return;
    }

    if (
      !canvasData.steps[stepId] &&
      canvasData.versions &&
      canvasData.versions.length > 0 &&
      !hasShownConfirmationRef.current
    ) {
      setShowConfirmation(true);
      hasShownConfirmationRef.current = true;
    }
  }, [
    stepId,
    selectedWorkflowId,
    canvasData.steps,
    canvasData.versions,
    isTemplate,
  ]);

  useEffect(() => {
    if (isTemplate) {
      return;
    }

    if (selectedWorkflowId) {
      dispatch(loadDraftWorkflow(selectedWorkflowId));
      dispatch(getLatestWorkflowVersions(selectedWorkflowId));
    }
  }, [dispatch, isTemplate, selectedWorkflowId]);

  useEffect(() => {
    if (isTemplate) {
      return;
    }
    if (canvasData?.intent_id) {
      dispatch(getIntent(canvasData?.intent_id as string));
    }
  }, [dispatch, canvasData?.intent_id, isTemplate]);

  return {
    cancelLoadSavedVersion: (event?: React.MouseEvent) => {
      event?.preventDefault();
      event?.stopPropagation();
      setShowConfirmation(false);
    },
    confirmLoadSavedVersion: (event?: React.MouseEvent) => {
      event?.preventDefault();
      event?.stopPropagation();

      const latestSavedVersion = canvasData.versions
        .filter(version => !version.is_draft)
        .toSorted((a, b) => b.version - a.version)?.[0];

      if (latestSavedVersion) {
        dispatch(
          getWorkflowVersion({
            intentWorkflowId: selectedWorkflowId,
            version: latestSavedVersion.version,
          }),
        );
      }

      setShowConfirmation(false);
    },
    isReady:
      (canvasData.intent_workflow_id === selectedWorkflowId &&
        canvasData.intent !== null &&
        !isContextVariablesLoading) ||
      isTemplate,
    showConfirmation,
  };
};

export const useGetIsTemplateIntentWorkflow = () => {
  const { search } = useLocation();

  const intentId = getWorkflowValueFromSearch(search, 'intentId');

  return intentId.startsWith(TEMPLATE_INTENT_ID);
};

export const useGetTemplateAutoflowWorkflow = (intentWorkflowId = '') => {
  const { search } = useLocation();
  const workflowId =
    intentWorkflowId || getWorkflowValueFromSearch(search, 'workflowId');

  const { data, isLoading } = useGetAutoflowTemplatesQuery(undefined, {
    skip: !workflowId,
  });

  return {
    isLoading,
    template: data?.find(
      template => template.intent_workflow_id === workflowId,
    ),
  };
};

export const useGetAutoflowWorkflowOrAutoflowTemplate = (
  intentWorkflowId = '',
) => {
  const isTemplateIntentWorkflow = useGetIsTemplateIntentWorkflow();

  const {
    data,
    isLoading: isLoadingAutoflowWorkflow,
    ...rest
  } = useGetAutoflowWorkflowQuery(intentWorkflowId, {
    skip: !intentWorkflowId || isTemplateIntentWorkflow,
  });
  const { isLoading: isLoadingAutoflowTemplate, template } =
    useGetTemplateAutoflowWorkflow();

  // Wrap template policy_description in a div to prevent Lexical root node errors
  const wrappedTemplate = template
    ? {
        ...template,
        policy_description: `<div>${template.policy_description}</div>`,
      }
    : undefined;

  return {
    autoflowWorkflow: wrappedTemplate ?? data,
    isLoading: isLoadingAutoflowWorkflow || isLoadingAutoflowTemplate,
    ...rest,
  };
};

export const useGetHandoffConfigurationForWorkflow = () => {
  const { intentId, view } = useGetBuilderQueryParams();
  const { data: handoffConfigurationsResponse } =
    useGetHandoffConfigurationsQuery();

  const handoffConfig = useMemo(() => {
    const handoffConfigurations =
      handoffConfigurationsResponse?.configurations ?? [];

    let product = CHANNEL_TO_PRODUCT_MAP[view];
    // email subflow is created with default workflow_builder product
    if (view === 'email') {
      product = 'workflow_builder';
    }
    return handoffConfigurations.find(
      handoffConfiguration =>
        handoffConfiguration.intent_definition_id === intentId &&
        handoffConfiguration.product === product,
    );
  }, [handoffConfigurationsResponse?.configurations, intentId, view]);

  return handoffConfig;
};

export const useSwapItem = ({
  accept = 'SWAP_ITEM',
  index,
  swap,
}: {
  accept?: string;
  index: number;
  swap: (dragIndex: number, hoverIndex: number) => void;
}) => {
  const ref = useRef<HTMLImageElement>(null);

  const [{ handlerId }, drop] = useDrop<
    { index: number },
    void,
    { handlerId: ReturnType<DropTargetMonitor['getHandlerId']> }
  >({
    accept,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },

    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) {
        return;
      }

      const newIndex = handleSwapWhenHovering({
        clientOffset,
        dragIndex,
        hoverBoundingRect,
        hoverIndex,
        swap,
      });

      if (newIndex === undefined) {
        return;
      }

      item.index = newIndex;
    },
  });

  const [{ opacity }, drag, dragPreviewRef] = useDrag({
    collect: monitor => ({
      opacity: monitor.isDragging() ? 0.3 : 1,
    }),
    item: () => {
      return { index };
    },
    type: accept,
  });

  drag(drop(ref));

  return { dragPreviewRef, handlerId, opacity, ref };
};

export const useGetCanvasActions = (
  {
    product,
    shouldReturnAll,
  }: {
    product: SolveWidgetProduct;
    shouldReturnAll?: boolean;
  } = { product: 'workflow_builder' },
) => {
  const {
    data: actionBuilderActionsData,
    isLoading: isActionBuilderActionsLoading,
  } = useGetActionBuilderActionsQuery({
    product,
    shouldReturnAll,
  });
  const { actions = [] } = actionBuilderActionsData ?? {};

  const transformedActions = useMemo(() => {
    return transformActionsForCanvas(actions);
  }, [actions]);

  return {
    actions: transformedActions,
    isLoading: isActionBuilderActionsLoading,
  } as const;
};

export const useWorkflowInvalidMessages = () => {
  const { data } = useGetIntentsQueryWithProduct();
  const { intents = [] } = data ?? {};
  const { data: actionBuilderActionsData, isLoading } =
    useGetActionBuilderActionsQuery({
      shouldReturnAll: true,
    });
  const { data: oauthClientsData } = useGetConversationOauthClientsQuery();
  const canvasData: CanvasWorkflowBuilderState = useSelector(
    selectCanvasWorkflowBuilder,
  );

  const invalidGoToSteps = useSelector(selectInvalidGoToSteps);
  const invalidIntentNames = invalidGoToSteps.map(invalidGoToStep => {
    const stepReferenced = canvasData.steps[invalidGoToStep];
    const intentWorkflowId = (
      stepReferenced.step_fields as { intent_workflow_id: string }
    ).intent_workflow_id;
    const foundIntent = intents.find(
      intent =>
        intent.intent_workflow_id === intentWorkflowId ||
        intent.interactive_email_workflow_id === intentWorkflowId ||
        intent.api_workflow_id === intentWorkflowId,
    );
    return foundIntent?.intent_name || '';
  });

  const dedupedInvalidIntentName = new Set(invalidIntentNames);

  const { contextVariables } = useGetContextVariables({
    shouldIncludeSystemContextVariables: true,
  });

  const [isAllBranchesFilled] = useSelector(
    selectIsBranchValid(canvasData.entryStepId),
  );
  const undefinedCvs: string[] = useSelector(selectUndefinedContextVariables);
  const unsupportedSteps = useSelector(selectUnsupportedSteps);
  const unsupportedStepNames = unsupportedSteps.map(unsupportedStep => {
    for (const canvasAction of Object.values(
      canvasData.canvas_action_id_to_action_component,
    )) {
      if (canvasAction.steps.includes(unsupportedStep)) {
        const foundAction = actionBuilderActionsData?.actions.find(
          action => action.action_id === canvasAction.action_id,
        );

        if (foundAction) {
          return `${foundAction.action_name} action`;
        }
      }
    }
    const stepReferenced = canvasData.steps[unsupportedStep];

    return `${stepReferenced.step_type} step`;
  });
  // an action could have multiple unsupported steps
  // we only want to show the action name once
  const dedupedUnsupportedStepNames = new Set(unsupportedStepNames);

  const undefinedContextVariableNames = undefinedCvs
    .filter(id => !id.startsWith(CONVERSATION_OAUTH_CLIENT_CV_PREFIX))
    .map(
      id =>
        contextVariables.find(
          contextVariable => contextVariable.context_variable_id === id,
        )?.context_variable_name,
    )
    .map(name => `$${name}`)
    .join(', ');

  const undefinedOauthSignInClientNames = getUndefinedOauthSignInClientNames({
    oauthClientsData,
    undefinedContextVariableIds: undefinedCvs,
  });

  return {
    invalidIntentNames: formatIntentNames(Array.from(dedupedInvalidIntentName)),
    isAllBranchesFilled,
    isLoading,
    undefinedContextVariableNames,
    undefinedOauthSignInClientNames,
    unsupportedStepNames: Array.from(dedupedUnsupportedStepNames).join(', '),
  };
};

export const useBuildInsightsChatNavigationCallback = (intentId?: string) => {
  const { data } = useGetIntentsQueryWithProduct();
  const navigate = useNavigate();
  const { search } = useLocation();

  const { channel } = useSolveMetricsQueryParams();
  // get the index of the Solve Insights Chats tab
  const { tabIdToIndexMap } = useTabs();
  const chatsTabIndex = tabIdToIndexMap.chat;

  return useCallback(() => {
    const conversationUrl = getChatUrl({
      channel,
      chatsTabIndex,
      // fixes weird issue where navigating with no id param
      // crashes the page. It is serialized to an empty string
      conversationId: 'null',
    });
    const currentQuery = new URLSearchParams(search);
    const defaultTimestamp = dateRangeToTimestamp(last30DaysTimeRange);
    const startTime = Number(
      currentQuery.get('start') || defaultTimestamp.start_timestamp,
    );
    const endTime = Number(
      currentQuery.get('end') || defaultTimestamp.end_timestamp,
    );

    navigate(
      conversationUrl +
        `&date=${startTime * 1000}_${endTime * 1000}${
          intentId
            ? `&${INTENT_FILTER_PARAM_NAME}=workflow_id.${
                data?.intents.find(
                  intent => intent.intent_definition_id === intentId,
                )?.intent_workflow_id
              }`
            : ''
        }`,
    );
  }, [channel, chatsTabIndex, data?.intents, intentId, navigate, search]);
};

export const useBuildVoiceInsightNavigationCallback = () => {
  const navigate = useNavigate();
  // get the index of the Solve Insights Chats tab
  const { tabIdToIndexMap } = useTabs();
  const chatsTabIndex = tabIdToIndexMap.chat;

  return useCallback(() => {
    navigate(
      `${Routes.SOLVE_INSIGHTS_MAIN}?tab=${chatsTabIndex}&product_mode=voice`,
    );
  }, [navigate, chatsTabIndex]);
};

export const useBuildWebCallInsightNavigationCallback = () => {
  const navigate = useNavigate();
  const { tabIdToIndexMap } = useTabs();
  const chatsTabIndex = tabIdToIndexMap.chat;

  return useCallback(() => {
    navigate(
      `${Routes.SOLVE_INSIGHTS_MAIN}?tab=${chatsTabIndex}&product_mode=web_call`,
    );
  }, [navigate, chatsTabIndex]);
};

export const useBuildEmailChatInsightNavigationCallback = () => {
  const navigate = useNavigate();
  // get the index of the Solve Insights Email Chats tab
  const { tabIdToIndexMap } = useTabs();
  const chatsTabIndex = tabIdToIndexMap.chat;

  return useCallback(() => {
    navigate(
      `${Routes.SOLVE_INSIGHTS_MAIN}?tab=${chatsTabIndex}&product_mode=email`,
    );
  }, [navigate, chatsTabIndex]);
};

export const useSetTargetStepId = (stepId: string) => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (stepId) {
      dispatch(setEmailTargetStepId(stepId));
    }
  }, [stepId, dispatch]);
};

/**
 * Hook to manage "on-deck" building blocks using a hot spare pattern.
 * Pre-creates building blocks in the background to eliminate latency when users add them.
 *
 * Usage:
 * 1. Call prepareOnDeckBuildingBlock() on page load (checks prep endpoint first)
 * 2. When user clicks "Add Building Block", call claimBuildingBlock()
 * 3. After successful claim, immediately call prepareOnDeckBuildingBlock() for the next one
 *
 * @param generateIntentName - Function to generate a unique name for the building block
 */
export const useOnDeckBuildingBlock = (generateIntentName: () => string) => {
  const { workflowId } = useGetBuilderQueryParams();
  const { emailMode } = useEmailMode();
  const { view } = useGetBuilderQueryParams();
  const product = useGetAgentProductFromChannel();

  // Email autopilot support
  const emailBuilderState = useSelector(selectEmailBuilderState);
  const isEmailAutopilot = view === 'email' && emailMode !== 'Interactive';
  const configurationId =
    emailBuilderState?.intentEmailConfiguration?.configuration_id;

  const { data: emailAutopilotWorkflow } = useGetOrCreateAutopilotWorkflowQuery(
    {
      configurationId: configurationId || '',
      version: emailBuilderState?.intentEmailConfiguration?.version ?? -1,
    },
    { skip: !isEmailAutopilot || !configurationId },
  );

  const [createIntent] = useCreateIntentMutation();
  const [claimBuildingBlockMutation] = useClaimBuildingBlockMutation();
  const [checkPrep] = useLazyCheckBuildingBlockPrepQuery();

  // Guard to prevent duplicate prep calls from race conditions
  const isPreparing = useRef(false);

  // Determine the parent workflow ID based on whether this is email autopilot or regular autoflow
  const parentWorkflowId = isEmailAutopilot
    ? emailAutopilotWorkflow?.autopilot_workflow_id
    : workflowId;

  /**
   * Check if an on-deck building block needs to be created, and create one if needed.
   * Uses a ref guard to prevent duplicate calls from race conditions.
   *
   * @param force - Skip the prep check and create directly (use after claiming)
   */
  const prepareOnDeckBuildingBlock = useCallback(
    async (force = false) => {
      if (!parentWorkflowId || isPreparing.current) {
        return;
      }

      isPreparing.current = true;

      try {
        let shouldCreate = force;

        if (!force) {
          // Check if we need to create an on-deck building block
          const { data } = await checkPrep({
            intentWorkflowId: parentWorkflowId,
          });
          shouldCreate = data?.should_create ?? false;
        }

        if (shouldCreate) {
          // Fire-and-forget - don't await, silently fail
          createIntent({
            is_on_deck: true, // Mark as on-deck for the hot spare pattern
            name: generateIntentName(),
            parent_autoflow_intent_workflow_id: parentWorkflowId,
            product,
          }).catch(() => {
            captureMessage('On-deck building block creation failed', {
              level: 'warning',
              tags: { feature: 'agent-builder' },
            });
          });
        }
      } catch {
        captureMessage('On-deck building block prep check failed', {
          level: 'warning',
          tags: { feature: 'agent-builder' },
        });
      } finally {
        isPreparing.current = false;
      }
    },
    [checkPrep, createIntent, generateIntentName, parentWorkflowId, product],
  );

  /**
   * Poll the claim endpoint until a building block is ready.
   * Falls back to creating a new intent synchronously if polling times out.
   *
   * @param intentName - The name for the building block (used in fallback)
   * @returns The claimed building block's workflow_id and intent_id, or null on complete failure
   */
  const claimBuildingBlock = useCallback(
    async (
      intentName: string,
    ): Promise<{
      intentId: string;
      intentWorkflowId: string;
    } | null> => {
      if (!parentWorkflowId) {
        return null;
      }

      const maxAttempts = 4; // ~2 seconds max
      const intervalMs = 500;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          const result = await claimBuildingBlockMutation({
            intentWorkflowId: parentWorkflowId,
          }).unwrap();

          if (result.ready && result.workflow_id && result.intent_id) {
            return {
              intentId: result.intent_id,
              intentWorkflowId: result.workflow_id,
            };
          }

          // Not ready yet, wait and retry
          await new Promise(resolve => setTimeout(resolve, intervalMs));
        } catch {
          // Network error during claim - wait and retry
          await new Promise(resolve => setTimeout(resolve, intervalMs));
        }
      }

      // Polling timed out - fall back to synchronous creation
      try {
        const response = await createIntent({
          name: intentName,
          parent_autoflow_intent_workflow_id: parentWorkflowId,
          product,
        }).unwrap();

        return {
          intentId: response.canvas.intent_id,
          intentWorkflowId: response.canvas.intent_workflow_id,
        };
      } catch {
        // Complete failure
        return null;
      }
    },
    [claimBuildingBlockMutation, createIntent, parentWorkflowId, product],
  );

  // Prepare on-deck building block on mount
  useEffect(() => {
    if (parentWorkflowId) {
      prepareOnDeckBuildingBlock();
    }
  }, [parentWorkflowId, prepareOnDeckBuildingBlock]);

  return {
    claimBuildingBlock,
    prepareOnDeckBuildingBlock,
  };
};
